import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { IOpenProjectUserDetails } from "../../common/interface";
import {
  getOpenProjectAssignee,
  updateOpenProjectTask,
} from "../../services/openProject.service";
import {customPopper } from "../CustomPopper";

interface IIssueInfo {
  href: string;
  lockVersion: number;
}

interface IOpenProjectAssigneeSelectorProps {
  assignee: IOpenProjectUserDetails;
  issueInfo: IIssueInfo;
  projectId: string;
  onAssigneeUpdated: () => void;
  label?: string;
  containerRef: React.RefObject<HTMLDivElement>;
}

const unassigned = {
  _type: "User",
  name: "Unassigned",
  email: "",
  avatar: "",
};

const OpenProjectAssigneeSelector: React.FC<
  IOpenProjectAssigneeSelectorProps
> = ({
  assignee,
  issueInfo,
  projectId,
  onAssigneeUpdated,
  label,
  containerRef,
}) => {
  const [users, setUsers] = useState<IOpenProjectUserDetails[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<IOpenProjectUserDetails[]>(
    []
  ); // Filtered users based on search
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedAssignee, setSelectedAssignee] =
    useState<IOpenProjectUserDetails | null>(assignee);

  // Fetch users from the Open Project API
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      const { data: fetchedUsers } = await getOpenProjectAssignee(projectId);
      const usersDetails = fetchedUsers?._embedded?.elements;
      if (usersDetails?.length) {
        setUsers(usersDetails || []);
        setFilteredUsers(usersDetails || []);
      }
      setLoading(false);
    };
    fetchUsers();
  }, [projectId, assignee]);

  // Update assignee in Open Project
  const updateAssignee = async (newAssignee: IOpenProjectUserDetails) => {
    if (
      newAssignee?._links?.self?.href === selectedAssignee?._links?.self?.href
    ) {
      return;
    }
    setLoading(true);
    try {
      const response = await updateOpenProjectTask({
        link: issueInfo?.href,
        lockVersion: issueInfo?.lockVersion,
        _links: {
          assignee: {
            href: newAssignee?._links?.self?.href ?? null,
          },
        },
      });
      if (response.statusCode === 200) {
        setSelectedAssignee(newAssignee);
        onAssigneeUpdated();
        toast.success("Assignee updated successfully");
      } else {
        toast.error("Failed to update assignee");
      }
    } catch {
      toast.error("Error updating assignee");
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on the search input
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    if (query) {
      const filtered = users.filter((user) =>
        user.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users); // Reset to all users when the search is cleared
    }
  };

  const popperComponent = customPopper(containerRef);

  const userOptions = [unassigned, ...filteredUsers];

  return (
    <Box className="assignee-selector">
      {label && <p className="label-text">{label}</p>}
      <Autocomplete
        sx={{
          "& .MuiInputBase-root": {
            padding: "0px",
            paddingLeft: "3px",
          },
        }}
        PopperComponent={popperComponent}
        value={selectedAssignee ?? unassigned}
        onChange={(_, newValue) => {
          if (newValue) {
            updateAssignee(newValue as unknown as IOpenProjectUserDetails);
          }
        }}
        options={userOptions}
        getOptionLabel={(option) => option?.name || ""}
        disableClearable
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            fullWidth
            onChange={handleSearchChange}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) =>
          option ? (
            <Box
              component="li"
              {...props}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Avatar
                src={option?.avatar}
                alt={option?.name}
                sx={{ mr: 1, height: 32, width: 32, bgcolor: "primary.main" }}
              />
              <Box>
                <strong className="label-text">{option?.name}</strong>
                <div>{option?.email}</div>
              </Box>
            </Box>
          ) : undefined
        }
        isOptionEqualToValue={(option, value) => option === value}
      />
    </Box>
  );
};

export default OpenProjectAssigneeSelector;
